import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface BlogDetailMeta {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  image: string;
  date: string;
  readTime: string;
  author: string;
  tags: string[];
  relatedSlugs: string[];
}

export interface TocItem {
  id: string;
  text: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export type ContentBlock =
  | { type: 'paragraph'; html: string }
  | { type: 'heading2'; text: string; id: string }
  | { type: 'heading3'; text: string; id: string }
  | { type: 'image'; src: string; alt: string }
  | { type: 'orderedList'; items: string[] }
  | { type: 'unorderedList'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'divider' };

export interface BlogDetailData {
  meta: BlogDetailMeta;
  toc: TocItem[];
  faq: FaqItem[];
  contentBlocks: ContentBlock[];
}

interface BlogPostRow {
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  image: string | null;
  date: string | null;
  read_time: string | null;
  read_time_vi: string | null;
  author: string | null;
  tags: string[] | null;
  related_slugs: string[] | null;
  toc: TocItem[] | null;
  faq: FaqItem[] | null;
  content: ContentBlock[] | null;
  title_vi: string | null;
  excerpt_vi: string | null;
  category_vi: string | null;
  tags_vi: string[] | null;
  toc_vi: TocItem[] | null;
  faq_vi: FaqItem[] | null;
  content_vi: ContentBlock[] | null;
}

export function useBlogDetail(slug: string) {
  const [row, setRow] = useState<BlogPostRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchDetail() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_blog_posts')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (err) throw err;
        if (!cancelled) setRow(data as BlogPostRow | null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load blog post');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (slug) fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [slug, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const data: BlogDetailData | null = row
    ? {
        meta: {
          slug: row.slug,
          title: loc(language, row.title, row.title_vi),
          excerpt: loc(language, row.excerpt ?? '', row.excerpt_vi),
          category: loc(language, row.category ?? '', row.category_vi),
          image: row.image ?? '',
          date: row.date ?? '',
          readTime: loc(language, row.read_time ?? '', row.read_time_vi),
          author: row.author ?? '',
          tags: loc(language, row.tags ?? [], row.tags_vi),
          relatedSlugs: row.related_slugs ?? [],
        },
        toc: loc(language, row.toc ?? [], row.toc_vi),
        faq: loc(language, row.faq ?? [], row.faq_vi),
        contentBlocks: loc(language, row.content ?? [], row.content_vi),
      }
    : null;

  return { data, loading, error, refresh };
}